import { ColDef, IRowNode } from 'ag-grid-community';
import { t } from 'i18next';
import { upperFirst } from 'lodash';

import { statusColorMap } from 'bom/constants';
import { LineTypeCell, PartAliasCell } from 'bom/elements';
import { BomPartType, ComponentStatus } from 'bom/enums';
import { BomPartCellHelper, BomPartHelper } from 'bom/helpers';
import { IBomPart, IElement } from 'bom/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { StatusCell, WarningTooltipForAgGrid } from 'core/elements';
import { AgGridFilterOption, WarningType } from 'core/enums';
import { getLabel, parseUnicode } from 'core/utils';
import { IComponentNote, IJob, IJobPart } from 'jobs/types';
import { ImageCell } from 'parts/elements';
import { PartNumberHelper } from 'parts/helpers';
import { PricingHelper } from 'pricing/helpers';

import {
  ActionsCell,
  CostCell,
  DescriptionCell,
  ElementsCell,
  ExtCostCell,
  NotesCell,
  PartNumberCell,
  PnbPricingCell,
  QuantityCell,
} from './cells';

export const columnDef = (
  job: IJob,
  element: IElement | undefined,
  onPartNumberClick: (jobPart: IJobPart) => void,
  isAlternateVersionEnabled: boolean,
  hasRowReordering = false,
  isDataLoadCompleted = false,
  costDecimals = PricingHelper.DEFAULT_COST_DECIMALS
) => {
  const hasElement = !!element?.id;

  const isEditable = (jobPart: IJobPart) => jobPart && jobPart.status !== ComponentStatus.DELETED;

  const isDescriptionEditable = (jobPart: IJobPart) =>
    isEditable(jobPart) && BomPartType.PART === jobPart.type && !jobPart.partArr?.verified;

  const isQuantityInlineEditable = (jobPart: IJobPart) => isEditable(jobPart) && hasElement;

  const lineColumn = hasElement
    ? null
    : {
        colId: 'line',
        headerName: '#',
        pinned: 'left',
        field: 'line',
        wrapText: false,
        suppressPaste: true,
        suppressFillHandle: true,
        suppressSizeToFit: false,
        suppressHeaderMenuButton: true,
        resizable: false,
        minWidth: 80,
        width: 80,
        maxWidth: 80,
        suppressColumnsToolPanel: true,
        suppressMovable: true,
        lockPinned: true,
        enableValue: true,
        enablePivot: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: false,
        rowDrag: ({ node, data }: { node: IRowNode<IJobPart>; data: IJobPart }) =>
          hasRowReordering &&
          isDataLoadCompleted &&
          !node?.group &&
          data.status !== ComponentStatus.DELETED,
        headerClass: 'paddingSmall',
        cellRendererParams: { placeholder: '' },
      };

  const elementsColumn = hasElement
    ? null
    : {
        colId: 'elements',
        headerName: t('headerElements'),
        field: 'elements',
        minWidth: 400,
        width: 400,
        maxWidth: 900,
        filter: 'agTextColumnFilter',
        valueFormatter: ({ value }) =>
          value && value.map((element: IElement) => element.name).join(', '),
        cellRenderer: ElementsCell,
        cellRendererParams: {
          isEditable,
        },
      };

  const alternateVendorPartNumberColumn = isAlternateVersionEnabled
    ? {
        colId: 'alternateVendorPartNumber',
        headerName: t('labelAlternateVendorPartNumber'),
        field: 'alternateVendorPartNumber',
        filter: 'agTextColumnFilter',
      }
    : null;

  return [
    lineColumn,
    {
      colId: 'actions',
      width: 80,
      maxWidth: 80,
      sortable: false,
      resizable: false,
      enableRowGroup: false,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: ActionsCell,
      cellRendererParams: { job, element, isEditable },
      cellClass: 'noDecorationCell',
    },
    {
      colId: 'image',
      headerName: t('headerImage'),
      field: 'partArr.defaultImage',
      sortable: false,
      resizable: false,
      enableRowGroup: false,
      suppressSizeToFit: false,
      suppressHeaderMenuButton: true,
      filter: false,
      suppressPaste: true,
      suppressFillHandle: true,
      pinned: 'left',
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall',
      maxWidth: 74,
      valueFormatter: ({ value }) => value?.alt,
      cellRenderer: ImageCell,
    },
    {
      colId: 'partNumber',
      headerName: t('headerPartNumber'),
      filter: 'agTextColumnFilter',
      field: 'partArr.number',
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 160,
      maxWidth: 400,
      enableValue: true,
      enablePivot: true,
      cellStyle: { alignItems: 'flex-start' },
      valueFormatter: ({ value }) => PartNumberHelper.toLabel(value),
      filterValueGetter: ({ data }) => {
        if (!data) {
          return '';
        }

        const { partArr, vendorPartNumber, alternateVendorPartNumber } = data as IBomPart;

        return `${partArr?.number} ${vendorPartNumber} ${alternateVendorPartNumber}`;
      },
      cellRenderer: PartNumberCell,
      cellRendererParams: {
        isEditable,
        onPartNumberClick,
      },
    },
    alternateVendorPartNumberColumn,
    {
      colId: 'manufacturer',
      headerName: t('headerManufacturer'),
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 120,
      width: 160,
      maxWidth: 190,
      valueGetter: ({ data }) => BomPartCellHelper.getManufacturerName(data),
      cellRendererParams: {
        textEllipsis: false,
      },
    },
    {
      colId: 'componentName',
      headerName: t('headerComponentName'),
      field: 'componentName',
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 150,
      width: 180,
      maxWidth: 250,
      valueGetter: ({ data }) => {
        if (BomPartHelper.hasComponentAssigned(data)) {
          return parseUnicode(data.componentName);
        }

        return DEFAULT_PLACEHOLDER;
      },
    },
    {
      colId: 'description',
      headerName: t('headerDescription'),
      field: 'description',
      autoHeight: true,
      filter: 'agTextColumnFilter',
      minWidth: 200,
      width: 355,
      maxWidth: 900,
      editable: ({ data }) => isDescriptionEditable(data),
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      valueGetter: ({ data }) => BomPartCellHelper.getDescription(data),
      cellRenderer: DescriptionCell,
    },
    elementsColumn,
    {
      colId: 'computedCostQuantity',
      headerName: hasElement ? t('headerQuantityPerElement') : t('headerBuyQuantity'),
      filter: 'agNumberColumnFilter',
      field: 'quantity',
      aggFunc: 'sumQty',
      minWidth: 95,
      width: 110,
      editable: ({ data }) => isQuantityInlineEditable(data),
      cellStyle: ({ data }) => ({
        textAlign: 'right',
        cursor: isEditable(data) ? 'pointer' : 'default',
      }),
      valueGetter: ({ data }) =>
        element?.id ? data?.quantitiesPerElement?.[element?.id] : data?.computedCostQuantity,
      cellRenderer: QuantityCell,
      cellRendererParams: {
        isEditable,
        element,
      },
    },
    {
      colId: 'uom',
      headerName: t('headerUom'),
      field: 'uom',
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      minWidth: 75,
      width: 100,
      suppressPaste: true,
      suppressFillHandle: true,
      editable: ({ data }) => isEditable(data),
      cellEditorSelector: ({ data }) => ({
        component: 'agRichSelectCellEditor',
        params: {
          values: data?.allowedUoms,
        },
      }),
    },
    {
      colId: 'notes',
      headerName: t('headerNotes'),
      field: 'notes',
      sortable: true,
      minWidth: 400,
      width: 400,
      maxWidth: 600,
      filter: 'agTextColumnFilter',
      valueGetter: ({ data }) => formatNotes(data, element),
      cellRenderer: NotesCell,
      cellRendererParams: { isRowEditable: isEditable, element },
    },
    {
      colId: 'computedPurchaseCost',
      headerName: t('headerUnitCost'),
      field: 'computedPurchaseCost',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      enableValue: true,
      enablePivot: true,
      cellClass: 'paddingSmallLeft',
      minWidth: 140,
      maxWidth: 220,
      tooltipComponent: WarningTooltipForAgGrid,
      tooltipComponentParams: () => ({ type: WarningType.ERROR }),
      tooltipValueGetter: ({ data }) =>
        BomPartHelper.hasExpiredCost(data) ? t('errorInvalidCost') : '',
      cellRenderer: CostCell,
      cellRendererParams: { costDecimals },
    },
    {
      colId: 'computedCostBase',
      headerName: t('headerBase'),
      field: 'computedCostBase',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      width: 120,
      maxWidth: 300,
      hide: true,
      cellStyle: { alignItems: 'flex-end' },
      cellRenderer: PnbPricingCell,
      cellRendererParams: {
        columnId: 'computedCostBase',
        costDecimals,
      },
    },
    {
      colId: 'computedCostAdder',
      headerName: t('headerAdder'),
      field: 'computedCostAdder',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      width: 120,
      maxWidth: 300,
      hide: true,
      cellStyle: { alignItems: 'flex-end' },
      cellRenderer: PnbPricingCell,
      cellRendererParams: {
        type: 'adder',
        columnId: 'computedCostAdder',
        costDecimals,
      },
    },
    {
      colId: 'computedExtPurchaseCost',
      headerName: t('headerExtPurchaseCost'),
      minWidth: 140,
      maxWidth: 180,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => data && (data.computedPurchaseCost || 0) * (data.quantity || 0),
      cellRenderer: ExtCostCell,
    },
    {
      colId: 'computedExtStandardCost',
      headerName: t('headerExtStandardCost'),
      minWidth: 140,
      maxWidth: 180,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }) => data && (data.computedCost || 0) * (data.quantity || 0),
      cellRenderer: ExtCostCell,
    },
    {
      colId: 'lineType',
      headerName: t('headerLineType'),
      sortable: false,
      suppressPaste: true,
      suppressFillHandle: true,
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall noDecorationCell',
      minWidth: 100,
      width: 100,
      maxWidth: 100,
      floatingFilter: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => t(`labelLineType${upperFirst(value)}`),
      },
      valueGetter: ({ data }) => BomPartCellHelper.getLineType(data),
      cellRenderer: LineTypeCell,
      cellStyle: { textAlign: 'center' },
    },
    {
      colId: 'alias',
      headerName: t('headerAlias'),
      filter: 'agTextColumnFilter',
      suppressPaste: true,
      suppressFillHandle: true,
      valueGetter: ({ data }) => BomPartCellHelper.getAlias(data),
      cellRenderer: PartAliasCell,
      minWidth: 150,
      maxWidth: 200,
    },
    {
      colId: 'status',
      headerName: t('headerStatus'),
      field: 'status',
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => getLabel(value),
      },
      cellRenderer: StatusCell,
      cellRendererParams: { colorMap: statusColorMap },
    },
  ].filter(Boolean) as ColDef[];
};

const formatNotes = (data: IJobPart, element?: IElement) => {
  const componentNotes = data?.componentNotes || [];
  const elementComponentNotes = element
    ? data?.elementComponentNotesMap[element.id] || []
    : Object.values(data?.elementComponentNotesMap || {}).flat();

  const notes = [...componentNotes, ...elementComponentNotes];

  if (!notes) {
    return '';
  }

  return notes.map((note: IComponentNote) => note.content).join(' ');
};
